import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, CheckSquare, Star, Mail, Shield, MessageSquare, 
  RefreshCw, Check, X, AlertCircle, Clock, Info, ChevronDown, 
  ChevronRight, Settings, Database, Share2, Wifi, Mic, Sliders
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { VoiceAgentStatusWidget } from '../voice';

interface ConnectionStatus {
  id: string;
  name: string;
  type: 'calendar' | 'tasks' | 'social' | 'reviews' | 'email' | 'auth' | 'chatbot' | 'crm' | 'voice' | 'risknumber';
  icon: React.ReactNode;
  status: 'connected' | 'disconnected' | 'error' | 'partial';
  lastSync?: Date;
  details?: string;
  accountName?: string;
  errorMessage?: string;
}

const ConnectionStatusOverview: React.FC = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState<ConnectionStatus[]>([
    {
      id: 'calendar',
      name: 'Calendar Integration',
      type: 'calendar',
      icon: <Calendar className="h-6 w-6 text-blue-600" />,
      status: 'disconnected'
    },
    {
      id: 'tasks',
      name: 'Tasks Sync',
      type: 'tasks',
      icon: <CheckSquare className="h-6 w-6 text-green-600" />,
      status: 'disconnected'
    },
    {
      id: 'social',
      name: 'Social Media',
      type: 'social',
      icon: <Share2 className="h-6 w-6 text-purple-600" />,
      status: 'disconnected'
    },
    {
      id: 'reviews',
      name: 'Review Sync',
      type: 'reviews',
      icon: <Star className="h-6 w-6 text-yellow-500" />,
      status: 'disconnected'
    },
    {
      id: 'email',
      name: 'Email Settings',
      type: 'email',
      icon: <Mail className="h-6 w-6 text-red-500" />,
      status: 'disconnected'
    },
    {
      id: 'crm',
      name: 'CRM Integration',
      type: 'crm',
      icon: <Database className="h-6 w-6 text-indigo-600" />,
      status: 'disconnected'
    },
    {
      id: 'risknumber',
      name: 'Risk Number API',
      type: 'risknumber',
      icon: <Sliders className="h-6 w-6 text-orange-600" />,
      status: 'disconnected',
      details: 'API not configured'
    },
    {
      id: 'voice',
      name: 'LeadWealth.ai',
      type: 'voice',
      icon: <Mic className="h-6 w-6 text-pink-600" />,
      status: 'connected',
      lastSync: new Date(),
      details: '3 active agents'
    },
    {
      id: 'auth',
      name: 'User Authorization',
      type: 'auth',
      icon: <Shield className="h-6 w-6 text-indigo-600" />,
      status: 'connected',
      lastSync: new Date(),
      details: 'Active session'
    },
    {
      id: 'chatbot',
      name: 'Chatbot Analytics',
      type: 'chatbot',
      icon: <MessageSquare className="h-6 w-6 text-teal-600" />,
      status: 'connected',
      lastSync: new Date(),
      details: 'Collecting data'
    }
  ]);
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'connection-details': true
  });

  // Load connection statuses on component mount
  useEffect(() => {
    if (user) {
      fetchConnectionStatuses();
    }
  }, [user]);

  const fetchConnectionStatuses = async () => {
    setIsRefreshing(true);
    
    try {
      // In a real implementation, this would fetch actual connection statuses
      // For this demo, we'll simulate API calls and update with mock data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate fetching connection statuses from various tables
      const updatedConnections = [...connections];
      
      // Check calendar connections
      const { data: calendarData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .in('provider', ['google_calendar', 'apple_calendar', 'outlook_calendar'])
        .limit(1);
      
      if (calendarData && calendarData.length > 0) {
        const calendarIndex = updatedConnections.findIndex(c => c.id === 'calendar');
        if (calendarIndex !== -1) {
          updatedConnections[calendarIndex] = {
            ...updatedConnections[calendarIndex],
            status: 'connected',
            lastSync: new Date(calendarData[0].credentials.lastSync || Date.now()),
            accountName: calendarData[0].credentials.accountName || 'Google Calendar',
            details: `${calendarData.length} account${calendarData.length > 1 ? 's' : ''} connected`
          };
        }
      }
      
      // Check task sync connections
      const { data: taskData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .in('provider', ['google_tasks', 'apple_reminders', 'microsoft_todo'])
        .limit(1);
      
      if (taskData && taskData.length > 0) {
        const taskIndex = updatedConnections.findIndex(c => c.id === 'tasks');
        if (taskIndex !== -1) {
          updatedConnections[taskIndex] = {
            ...updatedConnections[taskIndex],
            status: 'connected',
            lastSync: new Date(taskData[0].credentials.lastSync || Date.now()),
            accountName: taskData[0].credentials.accountName || 'Task Provider',
            details: `${taskData.length} account${taskData.length > 1 ? 's' : ''} connected`
          };
        }
      }
      
      // Check social media connections
      const { data: socialData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .in('provider', ['facebook', 'twitter', 'instagram', 'linkedin'])
        .limit(10);
      
      if (socialData && socialData.length > 0) {
        const socialIndex = updatedConnections.findIndex(c => c.id === 'social');
        if (socialIndex !== -1) {
          updatedConnections[socialIndex] = {
            ...updatedConnections[socialIndex],
            status: 'connected',
            lastSync: new Date(Math.max(...socialData.map(d => new Date(d.credentials.lastSync || 0).getTime()))),
            details: `${socialData.length} platform${socialData.length > 1 ? 's' : ''} connected`
          };
        }
      }
      
      // Check review platform connections
      const { data: reviewData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .in('provider', ['google_reviews', 'facebook_reviews', 'yelp_reviews'])
        .limit(10);
      
      if (reviewData && reviewData.length > 0) {
        const reviewIndex = updatedConnections.findIndex(c => c.id === 'reviews');
        if (reviewIndex !== -1) {
          updatedConnections[reviewIndex] = {
            ...updatedConnections[reviewIndex],
            status: 'connected',
            lastSync: new Date(Math.max(...reviewData.map(d => new Date(d.credentials.lastSync || 0).getTime()))),
            details: `${reviewData.length} platform${reviewData.length > 1 ? 's' : ''} connected`
          };
        }
      }
      
      // Check email provider connections
      const { data: emailData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .in('provider', ['email', 'gmail', 'outlook'])
        .limit(1);
      
      if (emailData && emailData.length > 0) {
        const emailIndex = updatedConnections.findIndex(c => c.id === 'email');
        if (emailIndex !== -1) {
          updatedConnections[emailIndex] = {
            ...updatedConnections[emailIndex],
            status: 'connected',
            lastSync: new Date(emailData[0].credentials.lastSync || Date.now()),
            accountName: emailData[0].credentials.accountName || 'Email Provider',
            details: `${emailData.length} provider${emailData.length > 1 ? 's' : ''} connected`
          };
        }
      }
      
      // Check CRM connections
      const { data: crmData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .in('provider', [
          'redtail_crm', 'wealthbox_crm', 'salesforce_crm', 'zoho_crm',
          'dynamics_crm', 'hubspot_crm', 'junxure_crm', 'practifi_crm',
          'uptick_crm', 'nexj_crm'
        ])
        .limit(10);
      
      if (crmData && crmData.length > 0) {
        const crmIndex = updatedConnections.findIndex(c => c.id === 'crm');
        if (crmIndex !== -1) {
          updatedConnections[crmIndex] = {
            ...updatedConnections[crmIndex],
            status: 'connected',
            lastSync: new Date(Math.max(...crmData.map(d => new Date(d.credentials.lastSync || 0).getTime()))),
            details: `${crmData.length} CRM system${crmData.length > 1 ? 's' : ''} connected`
          };
        }
      }
      
      // Check Risk Number API connection
      const { data: riskNumberData } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user?.id)
        .eq('provider', 'risknumber_api')
        .limit(1);
      
      if (riskNumberData && riskNumberData.length > 0) {
        const riskNumberIndex = updatedConnections.findIndex(c => c.id === 'risknumber');
        if (riskNumberIndex !== -1) {
          updatedConnections[riskNumberIndex] = {
            ...updatedConnections[riskNumberIndex],
            status: 'connected',
            lastSync: new Date(riskNumberData[0].credentials.lastSync || Date.now()),
            accountName: riskNumberData[0].credentials.accountName || 'Risk Number API',
            details: 'API configured and active'
          };
        }
      }
      
      // Simulate some errors or partial connections
      const randomConnection = updatedConnections[Math.floor(Math.random() * 5)];
      if (randomConnection.status === 'disconnected') {
        randomConnection.status = 'error';
        randomConnection.errorMessage = 'Authentication token expired';
      }
      
      setConnections(updatedConnections);
    } catch (error) {
      console.error('Error fetching connection statuses:', error);
      toast.error('Failed to refresh connection statuses');
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusColor = (status: ConnectionStatus['status']) => {
    switch (status) {
      case 'connected':
        return 'text-green-500';
      case 'partial':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      case 'disconnected':
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: ConnectionStatus['status']) => {
    switch (status) {
      case 'connected':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'disconnected':
      default:
        return <X className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatDuration = (date?: Date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 0) {
      return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    } else if (diffHour > 0) {
      return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    } else if (diffMin > 0) {
      return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    } else {
      return `${diffSec} second${diffSec > 1 ? 's' : ''} ago`;
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFixConnection = (connectionId: string) => {
    // In a real implementation, this would attempt to fix the connection
    toast.success(`Attempting to fix ${connections.find(c => c.id === connectionId)?.name}`);
    
    // Simulate fixing the connection
    setTimeout(() => {
      setConnections(prev => prev.map(conn => 
        conn.id === connectionId 
          ? { ...conn, status: 'connected', lastSync: new Date(), errorMessage: undefined } 
          : conn
      ));
      toast.success(`${connections.find(c => c.id === connectionId)?.name} connection fixed`);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Connection Status</h2>
          <p className="mt-1 text-sm text-gray-500">
            View and manage all system connections and integrations
          </p>
        </div>
        <Button
          onClick={fetchConnectionStatuses}
          className="py-1.5 px-3"
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Connection Status Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div 
          className="flex items-center justify-between mb-4 cursor-pointer"
          onClick={() => toggleSection('connection-details')}
        >
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Wifi className="h-5 w-5 text-primary mr-2" />
            Connection Status Overview
          </h3>
          <button className="text-gray-400 hover:text-gray-600">
            {expandedSections['connection-details'] ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>

        {expandedSections['connection-details'] && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {connections.map(connection => (
                <div 
                  key={connection.id}
                  className={`border rounded-lg p-4 ${
                    connection.status === 'error' 
                      ? 'border-red-300 bg-red-50' 
                      : connection.status === 'connected'
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      {connection.icon}
                      <div className="ml-3">
                        <h4 className="font-medium text-gray-900">{connection.name}</h4>
                        <div className="flex items-center mt-1">
                          <span className={`text-xs ${getStatusColor(connection.status)}`}>
                            {connection.status.charAt(0).toUpperCase() + connection.status.slice(1)}
                          </span>
                          {connection.lastSync && (
                            <span className="text-xs text-gray-500 ml-2">
                              Last synced: {formatDuration(connection.lastSync)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {getStatusIcon(connection.status)}
                    </div>
                  </div>

                  {connection.details && (
                    <div className="mt-2 text-sm text-gray-600">
                      {connection.details}
                    </div>
                  )}

                  {connection.errorMessage && (
                    <div className="mt-2 text-sm text-red-600 flex items-start">
                      <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span>{connection.errorMessage}</span>
                    </div>
                  )}

                  {connection.status === 'error' && (
                    <div className="mt-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-xs py-1 h-auto w-full"
                        onClick={() => handleFixConnection(connection.id)}
                      >
                        Fix Connection
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Connection Health Summary</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Total Connections</div>
                    <div className="text-2xl font-bold">{connections.length}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Connected</div>
                    <div className="text-2xl font-bold text-green-600">
                      {connections.filter(c => c.status === 'connected').length}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Disconnected</div>
                    <div className="text-2xl font-bold text-gray-600">
                      {connections.filter(c => c.status === 'disconnected').length}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Errors</div>
                    <div className="text-2xl font-bold text-red-600">
                      {connections.filter(c => c.status === 'error').length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Voice Agent Status Widget */}
      <VoiceAgentStatusWidget />

      {/* Connection Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Details</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {connections.map(connection => (
                  <tr key={connection.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {connection.icon}
                        <span className="ml-2 font-medium text-gray-900">{connection.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(connection.status)}
                        <span className={`ml-2 ${getStatusColor(connection.status)}`}>
                          {connection.status.charAt(0).toUpperCase() + connection.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {connection.lastSync ? formatDuration(connection.lastSync) : 'Never'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {connection.details || (connection.status === 'error' ? connection.errorMessage : 'No details available')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-xs py-1 h-auto"
                        onClick={() => {
                          // Navigate to the specific settings tab
                          const tabElement = document.querySelector(`[data-tab="${connection.id}"]`);
                          if (tabElement) {
                            (tabElement as HTMLButtonElement).click();
                          } else if (connection.id === 'voice') {
                            window.location.href = '/dashboard/voice-agents';
                          } else if (connection.id === 'risknumber') {
                            const riskNumberTab = document.querySelector(`[data-tab="risknumber"]`);
                            if (riskNumberTab) {
                              (riskNumberTab as HTMLButtonElement).click();
                            }
                          }
                        }}
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Configure
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Troubleshooting</h3>
        
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Common Connection Issues</h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2" />
                <span><strong>Authentication Errors:</strong> Try reconnecting the service to refresh authentication tokens.</span>
              </li>
              <li className="flex items-start">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2" />
                <span><strong>API Rate Limits:</strong> Some services may temporarily limit connections due to high usage.</span>
              </li>
              <li className="flex items-start">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2" />
                <span><strong>Permission Changes:</strong> Service providers may have updated their permission requirements.</span>
              </li>
            </ul>
          </div>
          
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Quick Fixes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <Button variant="outline" className="flex items-center justify-center">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh All Connections
              </Button>
              <Button variant="outline" className="flex items-center justify-center">
                <Database className="h-4 w-4 mr-2" />
                Clear Connection Cache
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">About Connections</h4>
            <p className="mt-1 text-sm text-blue-700">
              This overview shows the status of all your system connections and integrations. 
              You can manage individual connections by clicking on the Configure button or 
              navigating to the specific settings tab. Regular synchronization ensures your 
              data stays up-to-date across all platforms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionStatusOverview;